/**
 * PayoutStatusChip (Plan 20).
 *
 * Compact status badge for a `VendorPayout` envelope. Renders the
 * Plan-20 status tone mapping (PENDING gray / SENT blue / PAID green
 * / FAILED red / HELD yellow) via `@ordo/ui/badge`.
 *
 * Edge case: when the status is HELD the chip is clickable and
 * deep-links to the BlockingTask inbox so the chef can see what's
 * holding the payout (Plan 20 edge-case spec).
 */

import { Badge } from '@ordo/ui/badge'
import { Link } from 'react-router'

import type { PayoutStatus } from '@/api/billing-kitchen'

import { payoutStatusLabel, payoutStatusTone } from './kitchen-payouts-helpers'

export interface PayoutStatusChipProps {
  status: PayoutStatus
  /** Optional title text — e.g. the failureReason / heldReason. */
  title?: string | null
  /**
   * When true (default), HELD chips link to the payment-blocking
   * task inbox. Pass false on the history table where the chip is
   * embedded inside a row click target.
   */
  linkHeldToTasks?: boolean
}

export function PayoutStatusChip({ status, title, linkHeldToTasks = true }: PayoutStatusChipProps) {
  const tone = payoutStatusTone(status)
  const label = payoutStatusLabel(status)
  const chip = (
    <Badge variant={tone} title={title ?? undefined}>
      {label}
    </Badge>
  )
  if (status === 'HELD' && linkHeldToTasks) {
    return (
      <Link
        to="/tasks?subKind=PAYMENT_BLOCKING"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-full">
        {chip}
      </Link>
    )
  }
  return chip
}
